import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PortalRing from './PortalRing';
import CreateRing from './CreateRing';
import PostingRing from './PostingRing';
import storyService from '../../services/storyService';
import squareService from '../../services/squareService';
import { useAuth } from '../../context/AuthContext';
import { usePortalPosting } from '../../context/PortalPostingContext';
import useMediaQuery from '../../utils/useMediaQuery';
import { getCity } from '../../services/userLocation';
import { REELS_CHANGED, countdownLabel, introSub, introTag, isIntroReel, ringStateFor, ringFace, firstWords, ringCity, cityTone } from './reelRing';

/**
 * PORTALS — the ring rail at the top of /featured.
 * Design handoff §2 (docs/plans/2026-09-portals-stories/handoff/README.md), reordered by the
 * events-wall handoff §3. The order is fixed:
 *
 *   SEND → CITY PORTAL → introductions → live Portals, newest first → everyone else's reels
 *
 * SEND moved to the head (Sept 23 2026): its fan opens down-right, and at the tail of a full
 * rail it opened off the screen.
 *
 * City Portal replaced the "Feeling social?" card and is never moved or dropped; its tag counts
 * who's in the local square or posting to it. The live rings replaced the LIVE NOW strip. Public
 * signals ARE Portals (IsPortalSignal, scope public), so the reels already carry every one near
 * the viewer — the strip's separate /signals/public read would only have duplicated them. SEND is
 * always there, after the live ones.
 *
 * The rail is per-viewer: who is on it, what they sent and what you've already watched are all
 * resolved server-side for whoever is asking. That is why it loads on its own rather than
 * riding in the wall's shared, cacheable payload.
 */

/** Live now: a Portal you haven't watched yet, or your own. Everything else is "other reels". */
function isLiveNow(reel) {
  return !!reel.portal && (reel.isOwn || !!(reel.hasUnseen || reel.HasUnseen));
}
const sentAt = (reel) => new Date(reel.portal?.createdAtUtc || reel.portal?.startUtc || 0).getTime();

export default function StoriesRail({
  location = null,
  onOpenReel = null,
  here = undefined,
  /** Scope the rail to one club's Portals — the club page's own board. */
  clubId = null,
  /** The club board draws its own panel, so the rail brings no border there. */
  bordered = true,
  /**
   * 'square' is the City Portal's rail (town-square handoff §4): headed PORTALS · OPEN IN
   * <CITY> with a pulsing N LIVE, no City Portal door (you're standing in it), OPEN ONE first,
   * then live Portals newest first, club Portals, and the ones you've watched.
   */
  variant = 'wall',
  cityLabel = '',
  /** Square only: OPEN ONE takes you to the page's own composer. */
  onOpenOne = null,
  /** Hands the page the rail's order, so its other doors open the same viewer sequence. */
  onLoaded = null,
  /** Square only: Home's rings are larger than the wall's. */
  ringSize = null,
}) {
  const isSquare = variant === 'square';
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  // Your own Portal, still going up. Nobody else's rail has it yet — and the face it wears
  // comes with it, so the rail never has to go looking for who you are.
  const { posting } = usePortalPosting();
  // Events-wall handoff: 64px rings and 16px gutters on phones (< 640), 72 / 28 elsewhere.
  const phone = useMediaQuery('(max-width: 639px)');
  const isDesktop = !phone;
  const padX = isSquare ? (phone ? 18 : 48) : (phone ? 16 : 28);
  const [reels, setReels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, tick] = useState(0);        // re-render so countdowns stay honest
  const scrollerRef = useRef(null);
  const [overflowing, setOverflowing] = useState(false);

  const size = ringSize || (isDesktop ? 72 : 64);
  const gap = isDesktop ? 16 : 12;

  const load = useCallback(async () => {
    // A club's board is the club's Portals; every other rail is a city's, so it carries that town's
    // introductions.
    const r = await storyService.getReels({ lat: location?.lat ?? null, lng: location?.lng ?? null, clubId, introductions: !clubId, city: getCity() });
    if (r.success) setReels(r.data || []);
    setLoading(false);
  }, [location, clubId]);

  useEffect(() => { load(); }, [load]);

  // Your Portal just went up: its row was on the rail as words-only (it's created before the clip
  // uploads), so fetch again now and the ring, the face and the viewer have the real thing —
  // no refresh.
  useEffect(() => {
    if (posting?.status === 'up') load();
  }, [posting?.status, load]);

  // An introduction left or arrived while a viewer or sheet was over the rail.
  useEffect(() => {
    window.addEventListener(REELS_CHANGED, load);
    return () => window.removeEventListener(REELS_CHANGED, load);
  }, [load]);

  // City Portal headcount. Read-only: looking at the door doesn't put you in the room. A page
  // that already asked (the wall, for its NEAR line) passes `here` and the rail doesn't ask again.
  const [ownHere, setOwnHere] = useState(null);
  const squareHere = here !== undefined ? here : ownHere;
  const squareCount = squareHere?.count || 0;
  // The door is labelled with the town it opens onto (rail handoff 1b) — CITY PORTAL only
  // until the nearest square has answered.
  const squareLabel = (squareHere?.label || '').split(',')[0].trim().toUpperCase() || 'CITY PORTAL';
  useEffect(() => {
    if (here !== undefined) return undefined;
    let cancelled = false;
    squareService.getHere({ lat: location?.lat ?? null, lng: location?.lng ?? null })
      .then((r) => { if (!cancelled && r?.success) setOwnHere(r.data || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [location, here]);

  // A Portal dies at its expiry, not at some day boundary — drop it the moment it does.
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  const live = useMemo(
    () => reels.filter((r) => !r.expiresAtUtc || new Date(r.expiresAtUtc).getTime() > Date.now()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reels, Math.floor(Date.now() / 30_000)]
  );

  const mine = live.find((r) => r.isOwn) || null;
  const others = live.filter((r) => !r.isOwn);

  // Rail order after City Portal. While your own Portal is still going up, the posting ring
  // stands in for it, so it's left out of the live group.
  const postingNow = !!posting && posting.status !== 'up';
  const liveGroup = useMemo(
    () => live.filter((r) => isLiveNow(r) && !(r.isOwn && postingNow)).sort((a, b) => sentAt(b) - sentAt(a)),
    [live, postingNow]
  );
  const restGroup = useMemo(
    () => live.filter((r) => !isLiveNow(r) && !isIntroReel(r) && !(r.isOwn && postingNow)),
    [live, postingNow]
  );
  const introGroup = useMemo(() => live.filter(isIntroReel), [live]);
  const ordered = useMemo(() => (isSquare
    ? [...introGroup, ...liveGroup.filter((r) => !r.isClub), ...liveGroup.filter((r) => r.isClub), ...restGroup]
    : [...introGroup, ...liveGroup, ...restGroup]), [introGroup, liveGroup, restGroup, isSquare]);

  useEffect(() => { if (!loading && onLoaded) onLoaded(ordered); }, [ordered, loading, onLoaded]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    const measure = () => setOverflowing(el.scrollWidth > el.clientWidth + 4);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [live.length, size]);

  // The viewer needs the whole rail, not just the ring that was tapped — swiping across goes
  // to the next person, so it has to know who that is.
  // City Portal isn't part of the viewer's sequence — it's a door, not a Portal.
  // …and the ring's real on-screen rect, because that exact ring is the doorway the viewer
  // falls out of. Half-scrolled off the edge is still where the door is.
  const openReel = (reel, event) => {
    const ring = event?.portalRingRect || event?.currentTarget?.getBoundingClientRect?.();
    if (onOpenReel) onOpenReel(reel.authorUserId, ordered, ring ? { left: ring.left, top: ring.top, width: ring.width, height: ring.height } : null);
  };

  const isPosting = !!posting && posting.status !== 'up';

  const liveCount = liveGroup.length;
  const railMeta = !isAuthenticated ? 'PUBLIC'
    : isPosting ? (posting.status === 'failed' ? 'YOURS \u00B7 NOT POSTED' : 'YOURS \u00B7 ONLY YOU SEE IT')
    : loading ? '…'
    : mine && liveCount === 1 && liveGroup[0]?.isOwn ? `YOURS · ${countdownLabel(mine.expiresAtUtc) || 'LIVE'} LEFT`
    : `${liveCount} LIVE`;

  const clubNote = liveCount === 0
    ? 'NO PORTALS ON THIS CLUB RIGHT NOW. OPEN ONE AND IT LANDS HERE.'
    : null;

  const note = clubId ? clubNote : !isAuthenticated
    ? "PUBLIC PORTALS ONLY. SIGN IN TO SEE YOUR FRIENDS’."
    : loading ? null
      : liveCount === 0 ? 'NO PORTALS NEAR YOU. OPEN ONE AND THE RAIL IS YOURS.'
        : (mine && others.length === 0) ? 'ONLY YOU SO FAR. TAP YOURS TO SEE HOW MANY WATCHED.'
          : null;

  const plusLabel = !isAuthenticated ? 'SIGN IN' : liveCount === 0 ? 'BE FIRST' : 'OPEN';

  return (
    <section
      className={bordered ? 'border-b-2 border-black bg-white' : 'bg-white'}
      // The extra top padding clears the FILTERS tab hanging from the top bar (26px): the rail sits
      // flush under the bar and the tab overlaps this padding (wall/FilterDrawer).
      style={{ padding: isSquare ? '16px 0 14px' : (phone ? '28px 0 12px' : '32px 0 18px') }}
      aria-label="Portals"
    >
      <div className={isDesktop ? `mx-auto ${isSquare ? 'max-w-[1240px]' : 'max-w-[1280px]'}` : ''}>
        {isSquare ? (
          <div className="flex items-baseline justify-between" style={{ padding: `0 ${padX}px 12px` }}>
            <span className="font-mono font-bold uppercase text-black" style={{ fontSize: 12, letterSpacing: '0.08em' }}>
              Portals{cityLabel && <span className="font-medium" style={{ color: '#6B7280' }}> · Open in {cityLabel}</span>}
            </span>
            <span className="flex items-center font-mono font-bold uppercase" style={{ gap: 6, fontSize: 10, letterSpacing: '0.1em', color: '#FF00AA' }}>
              <span className="pr-sq-pulse rounded-full" style={{ width: 7, height: 7, background: '#FF00AA' }} aria-hidden />
              {loading ? '…' : `${liveCount} LIVE`}
            </span>
          </div>
        ) : (
        <div
          className="flex items-baseline justify-between"
          style={{ padding: `0 ${padX}px 10px` }}
        >
          <span className="font-mono font-bold uppercase" style={{ fontSize: 12, letterSpacing: '0.05em', color: '#6B7280' }}>
            Portals
          </span>
          <span className="font-mono uppercase" style={{ fontSize: 10, letterSpacing: '0.05em', color: '#9CA3AF' }}>
            {railMeta}
          </span>
        </div>
        )}

        <div className="relative">
          <div
            ref={scrollerRef}
            className="flex items-start overflow-x-auto pr-rail-scroller"
            // 12px on top: the city tab is notched 8px above each ring (handoff 1b).
            style={{ padding: `12px ${padX}px 8px`, gap }}
          >
            {/* Square: OPEN ONE first — the page's composer is right below. */}
            {isSquare && (
              <PortalRing
                state="send"
                size={size}
                label="OPEN ONE"
                onClick={() => (onOpenOne ? onOpenOne() : navigate('/portals/new'))}
                ariaLabel="Open a portal"
              />
            )}

            {/* 0 · SEND — at the head of the rail. Its fan opens down and to the right, so at
                the far end of a full rail it opened off the screen and nothing new could be
                started; here it is always reachable, however many Portals are up. */}
            {!isSquare && <CreateRing size={size} label={plusLabel} signedIn={isAuthenticated} />}

            {/* 1 · City Portal. */}
            {!isSquare && <PortalRing
              state="square"
              size={size}
              count={squareCount}
              label={squareLabel}
              onClick={() => navigate('/square')}
              ariaLabel={squareCount > 0 ? `City Portal, ${squareCount} in` : 'City Portal'}
            />}

            {/* Going up: a square with a progress stroke, ahead of everyone else's rings. It
                turns into the swirl ring only when the Portal is actually live. */}
            {isPosting && (
              <div className="flex flex-col items-center">
                <PostingRing
                  status={posting.status}
                  progress={posting.progress}
                  size={size}
                  avatarUrl={posting.avatarUrl || null}
                  initial={posting.initial || 'Y'}
                />
                <span
                  className="mt-1.5 block max-w-full truncate font-mono font-bold uppercase"
                  style={{ fontSize: size >= 72 ? 10 : 9, letterSpacing: '0.05em', color: posting.status === 'failed' ? '#EF4444' : '#000000', maxWidth: size + 8 }}
                >
                  {posting.status === 'failed' ? 'RETRY' : posting.status === 'finishing' ? 'FINISHING' : 'POSTING'}
                </span>
              </div>
            )}

            {/* 2 · Live Portals, newest first. 3 · Everyone else's reels. */}
            {loading && live.length === 0
              ? [0, 1, 2, 3].map((i) => (
                <div key={i} className="flex flex-col items-center">
                  <div
                    className="pr-rail-skeleton rounded-full"
                    style={{ width: size, height: size, background: '#E5E7EB' }}
                  />
                  <span className="mt-1.5 block" style={{ width: size * 0.7, height: 9, background: '#E5E7EB' }} />
                </div>
              ))
              : ordered.map((reel) => (
                <PortalRing
                  key={reel.authorUserId}
                  state={ringStateFor(reel)}
                  size={size}
                  avatarUrl={ringFace(reel).url}
                  focus={ringFace(reel).focus}
                  initial={(reel.displayName || '?')[0]}
                  countdown={countdownLabel(reel.expiresAtUtc)}
                  snippet={firstWords(reel.portal?.text)}
                  city={ringCity(reel)}
                  cityTone={cityTone(reel)}
                  label={reel.isOwn ? 'YOU' : (isIntroReel(reel) ? (reel.displayName || '').split(' ')[0] : (reel.handle || reel.displayName || '')).toUpperCase()}
                  tag={isIntroReel(reel) ? introTag(reel) : null}
                  sub={isIntroReel(reel) ? introSub(reel) : null}
                  onClick={(e) => openReel(reel, e)}
                  ariaLabel={isIntroReel(reel)
                    ? (reel.isOwn ? 'Your introduction' : `${reel.displayName}’s introduction`)
                    : reel.isOwn ? 'Your Portal' : `Portal from ${reel.handle || reel.displayName}`}
                />
              ))}


          </div>

          {/* There's more to the right. A fade and a chevron, not a scrollbar. */}
          {overflowing && (
            <div
              className="pointer-events-none absolute right-0 top-0 flex items-center justify-end"
              style={{
                width: 40,
                height: size,
                background: 'linear-gradient(to right, rgba(255,255,255,0), #FFF 70%)',
              }}
              aria-hidden
            >
              <svg width="10" height="16" viewBox="0 0 10 16" fill="none" stroke="#000" strokeWidth="2" strokeLinecap="square">
                <path d="M2 2l6 6-6 6" />
              </svg>
            </div>
          )}
        </div>

        {note && !isSquare && (
          <p
            className="font-mono uppercase"
            style={{ fontSize: 10, letterSpacing: '0.05em', color: '#6B7280', padding: `12px ${padX}px 0` }}
          >
            {note}
          </p>
        )}
      </div>
    </section>
  );
}
